import { useEffect, useRef } from 'react';

import { Button, cn } from '@ibas/ui';
import { ArrowRight } from 'lucide-react';

import { Magnetic, ScrollReveal, SplitReveal, TextStagger } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';

/** A single drifting node in the constellation field. */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export interface ConstellationHeroProps {
  className?: string;
}

/** Cap device pixel ratio so the canvas never over-renders on dense displays. */
const MAX_DPR = 1.75;
/** Radius (CSS px) inside which the pointer pushes nodes away. */
const REPEL_RADIUS = 120;
/** Max distance (CSS px) at which two nodes are joined by a proximity line. */
const LINK_DISTANCE = 132;

/**
 * ConstellationHero — the full-viewport landing hero.
 *
 * A live canvas particle field (the "aether-flow" constellation pattern,
 * recoloured to the IBAS palette) drifts across a deep navy field: gold nodes
 * joined by faint white proximity lines, with a gentle pointer repulsion that
 * makes the field feel alive and interactive. The RAF loop is capped to
 * DPR ~1.75, paused via IntersectionObserver whenever the hero scrolls
 * offscreen, and fully torn down on unmount.
 *
 * Motion intent: the constellation reads as "connect globally" — discrete
 * learners drawn into a networked whole — behind a composed, masked headline
 * entrance. Under reduced motion the loop never starts; a static navy gradient
 * backdrop stands in and all content renders immediately and legibly.
 */
export function ConstellationHero({ className }: ConstellationHeroProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const canvas = canvasRef.current;
    const section = sectionRef.current;
    if (!canvas || !section) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    let width = 0;
    let height = 0;
    let particles: Particle[] = [];
    let rafId = 0;
    let running = false;

    const pointer = { x: -9999, y: -9999, active: false };

    const buildParticles = (): void => {
      const count = Math.min(150, Math.max(36, Math.floor((width * height) / 14000)));
      particles = Array.from({ length: count }, (): Particle => {
        const angle = Math.random() * Math.PI * 2;
        const speed = 0.12 + Math.random() * 0.22;
        return {
          x: Math.random() * width,
          y: Math.random() * height,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
        };
      });
    };

    const resize = (): void => {
      const rect = section.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildParticles();
    };

    const step = (): void => {
      ctx.clearRect(0, 0, width, height);

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;

        if (pointer.active) {
          const dx = p.x - pointer.x;
          const dy = p.y - pointer.y;
          const dist = Math.hypot(dx, dy);
          if (dist < REPEL_RADIUS && dist > 0.001) {
            const force = (REPEL_RADIUS - dist) / REPEL_RADIUS;
            p.x += (dx / dist) * force * 1.9;
            p.y += (dy / dist) * force * 1.9;
          }
        }

        // Wrap around the edges so the field is seamless and never empties.
        if (p.x < 0) p.x += width;
        else if (p.x > width) p.x -= width;
        if (p.y < 0) p.y += height;
        else if (p.y > height) p.y -= height;
      }

      // Proximity lines — faint white (navy-tint over the navy field).
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.hypot(dx, dy);
          if (dist < LINK_DISTANCE) {
            const alpha = (1 - dist / LINK_DISTANCE) * 0.2;
            ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // Nodes — IBAS gold.
      ctx.fillStyle = 'rgba(198, 146, 20, 0.78)';
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.7, 0, Math.PI * 2);
        ctx.fill();
      }

      rafId = requestAnimationFrame(step);
    };

    const start = (): void => {
      if (running) return;
      running = true;
      rafId = requestAnimationFrame(step);
    };

    const stop = (): void => {
      running = false;
      cancelAnimationFrame(rafId);
    };

    const onPointerMove = (event: PointerEvent): void => {
      const rect = canvas.getBoundingClientRect();
      pointer.x = event.clientX - rect.left;
      pointer.y = event.clientY - rect.top;
      pointer.active = true;
    };
    const onPointerLeave = (): void => {
      pointer.active = false;
    };

    resize();

    const visibility = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) start();
        else stop();
      },
      { threshold: 0 },
    );
    visibility.observe(section);

    let resizeFrame = 0;
    const onResize = (): void => {
      cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(resize);
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('pointermove', onPointerMove, { passive: true });
    canvas.addEventListener('pointerleave', onPointerLeave);

    return () => {
      stop();
      cancelAnimationFrame(resizeFrame);
      visibility.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
    };
  }, [reduced]);

  return (
    <section
      ref={sectionRef}
      className={cn(
        'relative isolate flex min-h-[100svh] w-full items-center justify-center overflow-hidden bg-brand-navy',
        className,
      )}
    >
      {/* Static navy depth — always present; also the sole backdrop under reduced motion. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(120%_80%_at_50%_-10%,var(--color-brand-navy-2)_0%,var(--color-brand-navy)_60%)]"
      />

      {/* Live constellation field (skipped entirely under reduced motion). */}
      {!reduced && (
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 z-0 h-full w-full"
        />
      )}

      {/* Vignette to hold headline contrast over the brightest node clusters. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 bg-[radial-gradient(80%_60%_at_50%_45%,rgba(21,14,66,0.55)_0%,rgba(21,14,66,0)_70%)]"
      />

      <div className="relative z-10 mx-auto flex max-w-4xl flex-col items-center px-6 py-16 text-center">
        <TextStagger
          text="A global community"
          by="words"
          className="font-label text-xs tracking-[0.32em] text-brand-gold uppercase sm:text-sm"
        />

        <SplitReveal
          as="h2"
          by="lines"
          className="mt-6 font-display text-5xl leading-[1.05] text-primary-foreground sm:text-6xl md:text-7xl"
        >
          63+ nationalities. Six continents. One academy.
        </SplitReveal>

        <ScrollReveal from="up" delay={0.35}>
          <p className="mt-6 max-w-2xl text-lg text-primary-foreground/80 sm:text-xl">
            From our campus in Thalwil to learning centres worldwide — study connected, wherever you
            are.
          </p>
        </ScrollReveal>

        <ScrollReveal
          from="up"
          delay={0.5}
          className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center"
        >
          <Magnetic>
            {/* Gold CTA — a navy (primary) button would blend into this navy
                section; gold bg + navy text keeps it prominent. */}
            <Button size="lg" variant="secondary" className="group" data-cursor>
              Explore the IBAS experience
              <ArrowRight className="ml-2 size-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Button>
          </Magnetic>
        </ScrollReveal>
      </div>
    </section>
  );
}
